/**
 * The session image is described by a Dockerfile the user reads and edits
 * (§10.1). It is never handed to `docker build`: the micro-VM backend boots the
 * `FROM` image and replays the instructions inside it once, into a snapshot
 * (§8.2).
 *
 * That is why the supported set is small. Everything describing how a
 * *container* is launched — CMD, ENTRYPOINT, EXPOSE, VOLUME — has no meaning for
 * a VM Airlock boots itself, and COPY/ADD would break the first invariant:
 * nothing is mounted into a sandbox. Rejecting them by name, with the reason,
 * beats ignoring them silently and letting the user believe they took effect.
 *
 * Parsing lives in `shared` because the editor validates as you type and the
 * Supervisor validates before it builds. One grammar, one answer.
 */

export interface DockerfileIssue {
  /** 1-based line of the instruction, for the editor's gutter. */
  line: number;
  message: string;
}

export interface DockerfileRun {
  line: number;
  command: string;
  /** The variables declared *above* this step, as `docker build` would see them. */
  env: Record<string, string>;
}

export interface ParsedDockerfile {
  /** OCI reference from `FROM`, or null when the file has none. */
  image: string | null;
  /** Shell commands, in order, run as root while the snapshot is built. */
  run: DockerfileRun[];
  /** `ENV`/`ARG` — exported during the build and persisted into the image. */
  env: Record<string, string>;
  issues: DockerfileIssue[];
}

/**
 * One generated layer, described (§10.1/§15).
 *
 * The text alone was enough while the only reader was somebody who had gone
 * looking for it in workspace settings. It is not enough in the wizard, where
 * the image is put in front of a person who did not ask for a Dockerfile and is
 * being asked to approve one — so each block says which repositories asked for
 * it, what it does, and what it downloads while doing it.
 *
 * Derived at generation time rather than parsed back out of the text: the
 * generator is the only thing that knows *why* it emitted a block, and a parser
 * over its own output would have to be kept in step with it forever.
 */
export interface GeneratedLayer {
  /** The `# comment` heading the block carries in the file — its identity here too. */
  title: string;
  /** One sentence, in the second person: what this layer does and why it is here. */
  why: string;
  /** Repository names that asked for it; empty when the workspace as a whole did. */
  forProjects: string[];
  /** Hosts *this* layer downloads from — a subset of `domains` (§7.1). */
  domains: string[];
  /** The instructions themselves, without the heading. */
  body: string;
}

/** A session image proposed from a workspace's repositories (§10.1). */
export interface GeneratedDockerfile {
  dockerfile: string;
  /** Hosts the generated layers reach at build time — proposed, not added (§7.1). */
  domains: string[];
  /** What the generator could not settle on its own, shown next to the editor. */
  notes: string[];
  /**
   * The same layers the text carries, one entry each, in file order. A reader
   * that only wants the text ignores this; the wizard's image step is built on
   * it, and `toolchain.test.ts` holds it to the text so the two cannot drift.
   */
  layers: GeneratedLayer[];
}

/** Rejected by name, each with the reason it cannot apply to a booted micro-VM. */
const REJECTED: Record<string, string> = {
  COPY: 'COPY is not supported — nothing is mounted into a sandbox. Install from a package registry, or let the agent create the file.',
  ADD: 'ADD is not supported — nothing is mounted into a sandbox.',
  CMD: 'CMD is not supported — Airlock boots the VM and starts the agent itself.',
  ENTRYPOINT: 'ENTRYPOINT is not supported — Airlock boots the VM and starts the agent itself.',
  USER: 'USER is not supported — sessions always run as the non-root `agent` user.',
  WORKDIR: 'WORKDIR is not supported — the workspace root is fixed at /workspace.',
  EXPOSE: 'EXPOSE has no effect — reachability is decided by the egress policy.',
  VOLUME: 'VOLUME is not supported — a sandbox has no mounts.',
  HEALTHCHECK: 'HEALTHCHECK is not supported — session health is derived from hook events.',
  SHELL: 'SHELL is not supported — build steps always run under /bin/sh.',
  ONBUILD: 'ONBUILD is not supported — this image is never used as a base for another.',
  STOPSIGNAL: 'STOPSIGNAL is not supported — the VM is stopped by the hypervisor.',
  MAINTAINER: 'MAINTAINER is obsolete and has no effect here.'
};

/**
 * The reference is interpolated into a guest shell command, so it is held to the
 * characters an OCI reference can legally contain — no substitution, no spaces.
 */
export function isValidImageRef(ref: string): boolean {
  return ref.length > 0 && ref.length <= 200 && /^[a-zA-Z0-9._:/@-]+$/.test(ref);
}

export function parseDockerfile(text: string): ParsedDockerfile {
  const parsed: ParsedDockerfile = { image: null, run: [], env: {}, issues: [] };

  for (const { line, text: source } of logicalLines(text)) {
    const match = /^([A-Za-z][A-Za-z0-9_]*)(?:\s+([\s\S]*))?$/.exec(source);
    if (!match) {
      parsed.issues.push({ line, message: `Not an instruction: ${truncate(source)}` });
      continue;
    }
    const instruction = match[1].toUpperCase();
    const rest = (match[2] ?? '').trim();

    if (instruction in REJECTED) {
      parsed.issues.push({ line, message: REJECTED[instruction] });
      continue;
    }

    // The allowlist *is* this switch — what Airlock replays, and nothing else.
    // A `SUPPORTED_INSTRUCTIONS` array used to say the same thing beside it with
    // no reference anywhere in the tree, free to drift from the parser.
    switch (instruction) {
      case 'FROM':
        readFrom(parsed, line, rest);
        break;
      case 'RUN':
        readRun(parsed, line, rest);
        break;
      case 'ENV':
      case 'ARG':
        readEnv(parsed, line, rest, instruction);
        break;
      case 'LABEL':
        break; // parses, does nothing — there is no OCI image to label
      default:
        parsed.issues.push({ line, message: `Unknown instruction \`${instruction}\`.` });
    }
  }

  if (!parsed.image && !parsed.issues.some((i) => i.line === 0)) {
    parsed.issues.unshift({ line: 0, message: 'No FROM instruction — the image has no base to boot from.' });
  }
  return parsed;
}

function readFrom(parsed: ParsedDockerfile, line: number, rest: string): void {
  if (parsed.image !== null) {
    parsed.issues.push({
      line,
      message: 'Only one FROM is supported — a session boots a single image, not a build graph.'
    });
    return;
  }
  const [ref, ...tail] = rest.split(/\s+/);
  if (tail.length) {
    parsed.issues.push({ line, message: 'Build stages (`FROM … AS …`) and platform flags are not supported.' });
    return;
  }
  if (!ref || !isValidImageRef(ref)) {
    parsed.issues.push({ line, message: `Not a usable image reference: ${truncate(ref ?? '')}` });
    return;
  }
  parsed.image = ref;
}

function readRun(parsed: ParsedDockerfile, line: number, rest: string): void {
  if (!rest) {
    parsed.issues.push({ line, message: 'RUN has no command.' });
    return;
  }
  if (rest.startsWith('--')) {
    parsed.issues.push({ line, message: 'RUN flags (--mount, --network) are not supported.' });
    return;
  }
  // The JSON exec form bypasses the shell, which is the only thing we run.
  if (rest.startsWith('[')) {
    parsed.issues.push({ line, message: 'The exec form (`RUN ["a","b"]`) is not supported — use the shell form.' });
    return;
  }
  parsed.run.push({ line, command: rest, env: { ...parsed.env } });
}

function readEnv(parsed: ParsedDockerfile, line: number, rest: string, instruction: 'ENV' | 'ARG'): void {
  const tokens = tokenize(rest);
  if (!tokens.length) {
    parsed.issues.push({ line, message: `${instruction} has no variable.` });
    return;
  }

  // `ENV KEY value with spaces` — the legacy single-variable form.
  if (!tokens[0].includes('=') && tokens.length > 1) {
    assign(parsed, line, tokens[0], tokens.slice(1).join(' '));
    return;
  }

  for (const token of tokens) {
    const at = token.indexOf('=');
    if (at < 0) {
      // `ARG NAME` with no default: nothing to export, and no build arguments to
      // supply one — saying so beats silently exporting an empty string.
      parsed.issues.push({
        line,
        message: `${instruction} \`${token}\` has no value. Give it a default (\`${token}=…\`).`
      });
      continue;
    }
    assign(parsed, line, token.slice(0, at), token.slice(at + 1));
  }
}

function assign(parsed: ParsedDockerfile, line: number, key: string, value: string): void {
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
    parsed.issues.push({ line, message: `Not a usable variable name: ${truncate(key)}` });
    return;
  }
  parsed.env[key] = unquote(value);
}

/**
 * Physical lines folded into instructions: a trailing backslash continues, and a
 * comment line inside a continuation is dropped, as `docker build` does.
 */
function logicalLines(text: string): { line: number; text: string }[] {
  const out: { line: number; text: string }[] = [];
  const lines = text.split(/\r?\n/);
  let buffer = '';
  let start = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const trimmed = raw.trim();
    if (trimmed.startsWith('#')) continue;
    if (!buffer) {
      if (!trimmed) continue;
      start = i + 1;
    }
    const continued = /\\$/.test(trimmed);
    const piece = continued ? trimmed.slice(0, -1).trim() : trimmed;
    buffer = buffer ? `${buffer} ${piece}` : piece;
    if (!continued) {
      out.push({ line: start, text: buffer });
      buffer = '';
    }
  }
  if (buffer) out.push({ line: start, text: buffer });
  return out;
}

/** Whitespace split that keeps quoted values together. */
function tokenize(text: string): string[] {
  const out: string[] = [];
  let current = '';
  let quote: '"' | "'" | null = null;
  let held = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      if (ch === quote) quote = null;
      else current += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      held = true;
      continue;
    }
    if (/\s/.test(ch)) {
      if (current || held) out.push(current);
      current = '';
      held = false;
      continue;
    }
    current += ch;
  }
  if (current || held) out.push(current);
  return out;
}

function unquote(value: string): string {
  const quoted = /^(["'])([\s\S]*)\1$/.exec(value);
  return quoted ? quoted[2] : value;
}

function truncate(value: string): string {
  return value.length > 60 ? `${value.slice(0, 57)}…` : value;
}

/**
 * What a workspace inherits until it overrides it, and what the generator builds
 * on. The toolchain here is Airlock's own: `claude` is the agent, tmux is how the
 * terminal survives a detached VM (§5.3), and bash-completion is what makes the
 * dock shell usable. Editing it is allowed — it is the user's image — but a
 * session without `claude` will not start.
 */
export const BASE_DOCKERFILE = `# Session image. Airlock boots this image and replays the instructions below
# once, into a reusable snapshot — FROM, RUN, ENV and ARG are what apply.
# The base is named by digest, so two machines, or two dates, build the same
# image under the same snapshot key. Bump the digest with the tag.
FROM node:26-trixie-slim@sha256:e4db4c914bf8ad241bd24804fc25d272d89bad97e68cdc8a1414862790a02c39

# Airlock's own toolchain. Removing \`claude\` stops sessions from starting;
# \`codex\` is the second agent (§5.10) and only Codex sessions need it.
# Python is here for the agents rather than for a project: a one-off script is
# how an agent reads a CSV or rewrites fifty files, whatever the repository is.
RUN apt-get update -qq \\
 && apt-get install -y -qq --no-install-recommends \\
      git tmux nano bash-completion ca-certificates curl gnupg procps less \\
      python3 python3-venv python3-pip \\
 && rm -rf /var/lib/apt/lists/*
RUN npm install -g --silent @anthropic-ai/claude-code @openai/codex
`;

/**
 * The `FROM` lines Airlock shipped before the current one, newest first: Node 22
 * on Debian bookworm until 2026-10-08, when the base moved to the newest Node LTS
 * on Debian trixie (Codex 0211/node-newest-lts), then Node 24 on trixie until
 * 2026-11-01, when Node 26 became that LTS.
 */
const SHIPPED_BASES = [
  'FROM node:24-trixie-slim@sha256:173f125896c3b47ddf056734c7ea789d04595a6a08769a8f78e0df642781fb66',
  'FROM node:22-bookworm-slim@sha256:83f487e0a63425e5b4d146fb5e5be574bcbe1b7b843d3ebafdd95eaf7767a7e5'
];

/**
 * A stored Dockerfile moved onto the current base (§10.1).
 *
 * The same terms as `withAgentPackages`: only when its `FROM` line is still
 * exactly one Airlock shipped, so a base somebody chose is left as they wrote
 * it. The new base changes the snapshot key, so each workspace's next session
 * builds its image once. Returns the text unchanged when there is nothing to do.
 */
export function withCurrentBase(text: string): string {
  const current = /^FROM .*$/m.exec(BASE_DOCKERFILE)?.[0];
  if (!current) return text;
  const lines = text.split('\n');
  const at = lines.findIndex((line) => SHIPPED_BASES.includes(line));
  if (at < 0) return text;
  lines[at] = current;
  return lines.join('\n');
}

/** The baseline's package line as Airlock shipped it before Python joined it. */
const SHIPPED_APT_LINE = /^ {6}git tmux nano bash-completion ca-certificates curl gnupg procps less \\$/m;

/**
 * A stored Dockerfile given the Python the baseline now carries (§10.1).
 *
 * The same terms as `withAgentPackages`: only when the package line is still
 * exactly the one Airlock shipped, so an image somebody edited is left as they
 * wrote it. Returns the text unchanged when there is nothing to do.
 */
export function withPython(text: string): string {
  const match = SHIPPED_APT_LINE.exec(text);
  if (!match) return text;
  const next = text.slice(match.index + match[0].length).split('\n')[1] ?? '';
  if (/\bpython3\b/.test(next)) return text;
  return text.replace(SHIPPED_APT_LINE, `${match[0]}\n      python3 python3-venv python3-pip \\`);
}

/**
 * A stored Dockerfile brought up to the agents this build can run (§5.10, §10.1).
 *
 * The agent install line is in the hand-written baseline rather than in a
 * generated block, so a Dockerfile stored before a second agent existed keeps
 * installing only the first one — and a session on the other agent then launches
 * a command the guest does not have and exits at once. That is not something to
 * discover as `[process exited]`.
 *
 * Repaired **only when the line is still exactly the one Airlock shipped**: the
 * image is the user's text (§10.1), so an edited line is left alone and the
 * missing CLI is reported at launch instead. Returns the text unchanged when
 * there is nothing to do, so a caller can compare and skip the write.
 */
export function withAgentPackages(text: string): string {
  const wanted = 'RUN npm install -g --silent @anthropic-ai/claude-code @openai/codex';
  const shipped = /^RUN npm install -g --silent @anthropic-ai\/claude-code$/m;
  if (text.includes('@openai/codex') || !shipped.test(text)) return text;
  return text.replace(shipped, wanted);
}

/**
 * Managed layer for the opt-in Docker-in-VM capability. It is appended rather
 * than edited into a person's Dockerfile so it stays an explicit product
 * choice, contributes to the snapshot key, and can be removed cleanly.
 * Debian's `docker.io` since trixie carries the daemon alone and only recommends
 * its client, `docker-cli`, which `--no-install-recommends` leaves out: dockerd
 * came up with no `docker` to ask it (spike `docker-renetwork`, 2026-11-02).
 * Older Debians and Ubuntus have no such package, the client being in `docker.io`.
 */
const DOCKER_RUNTIME_LAYER = `

# Airlock Docker runtime. Starts at VM boot for Testcontainers; never exposes the host daemon.
RUN if command -v apt-get >/dev/null 2>&1; then \\
      apt-get update -qq && apt-get install -y -qq --no-install-recommends docker.io && { command -v docker >/dev/null 2>&1 || apt-get install -y -qq --no-install-recommends docker-cli; } && rm -rf /var/lib/apt/lists/*; \\
    elif command -v apk >/dev/null 2>&1; then \\
      apk add --no-cache docker; \\
    else \\
      echo 'Docker for Testcontainers needs an apt- or apk-based session image' >&2; exit 1; \\
    fi
`;

export function withContainerRuntime(dockerfile: string, runtime: 'none' | 'docker'): string {
  return runtime === 'docker' && !dockerfile.includes('# Airlock Docker runtime.')
    ? `${dockerfile.trimEnd()}${DOCKER_RUNTIME_LAYER}`
    : dockerfile;
}

/**
 * Managed layer for the guest's DNS cache (§7.1), which the VM starts at boot.
 *
 * Its log is how the host learns which names the VM's policy refused, to ask
 * whether to allow one (desktop's `blocked-hosts.ts`). It was first added
 * because msb up to 0.7.6 bound a name only to its latest DNS answer, refusing
 * parallel downloads from rotating-DNS hosts; 0.7.7 keeps earlier answers
 * (`pnpm spike:net-burst`). The layer's text below still says so, and stays: it
 * is the image's snapshot key, and rewording it rebuilds every image.
 *
 * Never fails the build: an image this cannot install into keeps the runtime's
 * resolver, and only the refused-host prompt goes without.
 */
const DNS_CACHE_LAYER = `

# Airlock DNS cache. Started at VM boot so parallel lookups of one host share one answer.
RUN command -v dnsmasq >/dev/null 2>&1 \\
 || if command -v apt-get >/dev/null 2>&1; then \\
      apt-get update -qq && apt-get install -y -qq --no-install-recommends dnsmasq-base && rm -rf /var/lib/apt/lists/*; \\
    elif command -v apk >/dev/null 2>&1; then \\
      apk add --no-cache dnsmasq; \\
    else \\
      false; \\
    fi \\
 || echo 'No DNS cache in this image: parallel downloads from some hosts may be refused' >&2
`;

/**
 * The Dockerfile a VM is built from: the person's, then Airlock's own layers —
 * the DNS cache always, Docker when the workspace asked for it. Every reader of
 * a session image goes through this, because the text is the snapshot's key.
 */
export function withManagedLayers(dockerfile: string, runtime: 'none' | 'docker'): string {
  const cached = dockerfile.includes('# Airlock DNS cache.') ? dockerfile : `${dockerfile.trimEnd()}${DNS_CACHE_LAYER}`;
  return withContainerRuntime(cached, runtime);
}
